import React, { useState, useRef, useEffect } from 'react';
import { 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    Box, 
    Typography,
    IconButton,
    Paper
} from '@mui/material';
import { 
    CameraAltRounded, 
    PhotoLibrary, 
    Close, 
    CameraAlt,
    FlipCameraAndroid,
    Circle,
    GridOn
} from '@mui/icons-material';

const CameraCapture = ({ open, onClose, onCapture }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
    const [devices, setDevices] = useState([]);
    const [showGrid, setShowGrid] = useState(false);
    const [photoCount, setPhotoCount] = useState(0);
    const [isCapturing, setIsCapturing] = useState(false);
    const [flashEnabled, setFlashEnabled] = useState(false);

    useEffect(() => {
        if (open) {
            startCamera();
            enumerateDevices();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [open, facingMode]);

    const enumerateDevices = async () => {
        try {
            const deviceList = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = deviceList.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
        } catch (err) {
            console.error('Error enumerating devices:', err);
        }
    };

    const startCamera = async () => {
        try {
            setError(null);
            
            // Stop any existing stream
            stopCamera();

            const constraints = {
                video: {
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    facingMode: facingMode
                }
            };

            // Add torch/flash support if available
            if (flashEnabled && facingMode === 'environment') {
                constraints.video.torch = true;
            }

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                setIsStreaming(true);
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            let errorMessage = 'Unable to access camera. ';
            
            if (err.name === 'NotAllowedError') {
                errorMessage += 'Please allow camera access in your browser settings.';
            } else if (err.name === 'NotFoundError') {
                errorMessage += 'No camera found on this device.';
            } else if (err.name === 'NotSupportedError') {
                errorMessage += 'Camera not supported in this browser.';
            } else {
                errorMessage += 'Please check your camera connection and try again.';
            }
            
            setError(errorMessage);
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setIsStreaming(false);
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current || isCapturing) return;

        setIsCapturing(true);

        // Create capture flash effect
        const flashOverlay = document.createElement('div');
        flashOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: white;
            z-index: 9999;
            opacity: 0.8;
            pointer-events: none;
        `;
        document.body.appendChild(flashOverlay);

        setTimeout(() => {
            document.body.removeChild(flashOverlay);
        }, 150);

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to blob
        canvas.toBlob((blob) => {
            if (blob) {
                // Create a File object from the blob
                const timestamp = new Date().getTime();
                const file = new File([blob], `camera-capture-${timestamp}.jpg`, {
                    type: 'image/jpeg',
                    lastModified: timestamp
                });

                // Create a mock event object similar to file input change event
                const mockEvent = {
                    target: {
                        files: [file]
                    }
                };

                setPhotoCount(prev => prev + 1);
                onCapture(mockEvent);
                
                setTimeout(() => {
                    setIsCapturing(false);
                }, 300);
            }
        }, 'image/jpeg', 0.9);
    };

    const switchCamera = () => {
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    };

    const handleClose = () => {
        stopCamera();
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: { 
                    height: '100vh',
                    width: '100vw',
                    margin: 0,
                    maxHeight: 'none',
                    maxWidth: 'none',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    borderRadius: 0
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)',
                color: 'white',
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                borderBottom: 'none'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CameraAlt sx={{ fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Camera
                    </Typography>
                    {photoCount > 0 && (
                        <Box sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.9)',
                            borderRadius: '12px',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            {photoCount} captured
                        </Box>
                    )}
                </Box>
                
                {/* Top Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Grid Toggle */}
                    <IconButton 
                        onClick={() => setShowGrid(!showGrid)}
                        sx={{ 
                            color: showGrid ? '#4CAF50' : 'rgba(255,255,255,0.7)',
                            backgroundColor: showGrid ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255,255,255,0.1)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                        }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 3v6h6V3H3zm2 2h2v2H5V5zm6-2v6h6V3h-6zm2 2h2v2h-2V5zM3 11v6h6v-6H3zm2 2h2v2H5v-2zm8 0h2v2h-2v-2zm-2-2v6h6v-6h-6z"/>
                        </svg>
                    </IconButton>

                    {/* Flash Toggle (for mobile cameras) */}
                    {facingMode === 'environment' && (
                        <IconButton 
                            onClick={() => setFlashEnabled(!flashEnabled)}
                            sx={{ 
                                color: flashEnabled ? '#FFC107' : 'rgba(255,255,255,0.7)',
                                backgroundColor: flashEnabled ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255,255,255,0.1)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
                            </svg>
                        </IconButton>
                    )}

                    <IconButton 
                        onClick={handleClose}
                        sx={{ 
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {error ? (
                    <Box sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Box sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            backdropFilter: 'blur(10px)'
                        }}>
                            <CameraAlt sx={{ fontSize: 64, color: 'rgba(255,255,255,0.8)' }} />
                        </Box>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Camera Access Required
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, maxWidth: 400, lineHeight: 1.6 }}>
                            {error}
                        </Typography>
                        <Button 
                            variant="contained"
                            onClick={startCamera}
                            sx={{ 
                                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                                borderRadius: '25px',
                                px: 4,
                                py: 1.5,
                                fontSize: '16px',
                                fontWeight: 600,
                                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 12px 40px rgba(76, 175, 80, 0.4)'
                                }
                            }}
                        >
                            Enable Camera
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ 
                        position: 'relative', 
                        width: '100%', 
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#000',
                        overflow: 'hidden'
                    }}>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                        
                        {/* Grid Overlay */}
                        {showGrid && isStreaming && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: `
                                    linear-gradient(to right, transparent 33%, rgba(255,255,255,0.3) 33%, rgba(255,255,255,0.3) 33.5%, transparent 33.5%, transparent 66.5%, rgba(255,255,255,0.3) 66.5%, rgba(255,255,255,0.3) 67%, transparent 67%),
                                    linear-gradient(to bottom, transparent 33%, rgba(255,255,255,0.3) 33%, rgba(255,255,255,0.3) 33.5%, transparent 33.5%, transparent 66.5%, rgba(255,255,255,0.3) 66.5%, rgba(255,255,255,0.3) 67%, transparent 67%)
                                `,
                                pointerEvents: 'none'
                            }} />
                        )}
                        
                        {/* Center Controls */}
                        {isStreaming && (
                            <>
                                {/* Bottom Control Panel */}
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                                    backdropFilter: 'blur(20px)',
                                    p: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        width: '100%',
                                        maxWidth: 500
                                    }}>
                                        {/* Switch Camera */}
                                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                                            {devices.length > 1 && (
                                                <IconButton
                                                    onClick={switchCamera}
                                                    disabled={isCapturing}
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                        backdropFilter: 'blur(10px)',
                                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                                            transform: 'scale(1.05)'
                                                        },
                                                        '&:disabled': {
                                                            opacity: 0.5
                                                        }
                                                    }}
                                                >
                                                    <FlipCameraAndroid />
                                                </IconButton>
                                            )}
                                        </Box>
                                        
                                        {/* Main Capture Button */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <IconButton
                                                onClick={capturePhoto}
                                                disabled={isCapturing}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    backgroundColor: isCapturing ? 'rgba(255, 193, 7, 0.9)' : 'white',
                                                    border: '4px solid rgba(255, 255, 255, 0.4)',
                                                    boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.1)',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    '&:hover': {
                                                        backgroundColor: isCapturing ? 'rgba(255, 193, 7, 1)' : 'rgba(255, 255, 255, 0.95)',
                                                        transform: 'scale(1.05)',
                                                        boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.2)'
                                                    },
                                                    '&:active': {
                                                        transform: 'scale(0.95)'
                                                    },
                                                    '&:disabled': {
                                                        opacity: 0.7
                                                    },
                                                    transition: 'all 0.2s ease-in-out'
                                                }}
                                            >
                                                {isCapturing ? (
                                                    <Box sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                        animation: 'pulse 0.5s ease-in-out infinite alternate'
                                                    }} />
                                                ) : (
                                                    <Circle sx={{ fontSize: 45, color: '#f44336' }} />
                                                )}
                                            </IconButton>
                                        </Box>
                                        
                                        {/* Done Button */}
                                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                            {photoCount > 0 && (
                                                <Button
                                                    onClick={handleClose}
                                                    variant="contained"
                                                    sx={{
                                                        background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                                                        borderRadius: '25px',
                                                        px: 3,
                                                        py: 1,
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        minWidth: 80,
                                                        boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 6px 25px rgba(76, 175, 80, 0.5)'
                                                        }
                                                    }}
                                                >
                                                    Done
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        )}
                        
                        {/* Loading State */}
                        {!isStreaming && !error && (
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                color: 'white'
                            }}>
                                <Box sx={{
                                    width: 100,
                                    height: 100,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 2,
                                    backdropFilter: 'blur(10px)',
                                    animation: 'pulse 2s ease-in-out infinite'
                                }}>
                                    <CameraAlt sx={{ fontSize: 48 }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Initializing Camera...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
                
                {/* Hidden canvas for capturing */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </DialogContent>
            
            {/* Custom Styles */}
            <style jsx>{`
                @keyframes pulse {
                    0% { opacity: 1; transform: scale(1); }
                    100% { opacity: 0.7; transform: scale(1.05); }
                }
            `}</style>
        </Dialog>
    );
};

const ImageUploader = ({ handleCapture }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        // Detect if we're on desktop
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
        setIsDesktop(!isMobile);
    }, []);

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleCameraClick = () => {
        if (isDesktop) {
            // For desktop, open our custom camera interface
            setCameraOpen(true);
        } else {
            // For mobile, use the native camera
            document.getElementById('camera-input').click();
        }
        handleDialogClose();
    };

    const handleFileClick = () => {
        document.getElementById('file-input').click();
        handleDialogClose();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 0',
                width: '100%',
            }}
        >
            <Button variant="contained" color="primary" onClick={handleDialogOpen}>
                <CameraAltRounded /> Add Pictures
            </Button>

            {/* Hidden inputs for file and camera (mobile fallback) */}
            <input
                id="camera-input"
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleCapture}
                multiple
            />
            <input
                id="file-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleCapture}
                multiple
            />

            {/* Main dialog with options */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>Select an Option</DialogTitle>
                <DialogContent>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<CameraAltRounded />}
                        onClick={handleCameraClick}
                        sx={{ marginBottom: '10px' }}
                    >
                        {isDesktop ? 'Use Webcam' : 'Use Camera'}
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<PhotoLibrary />}
                        onClick={handleFileClick}
                    >
                        Upload from Device
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Desktop camera capture dialog */}
            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handleCapture}
            />
        </Box>
    );
};

export default ImageUploader;